import React, { useState } from 'react';
import Icon from '../AppIcon';
import Button from './Button';
import Select from './Select';
import { Checkbox } from './Checkbox';

const ProductFilter = ({ isOpen = false, onToggle, onFilterChange, className = "" }) => {
  const [filters, setFilters] = useState({
    category: '',
    manufacturer: '',
    certification: [],
    priceRange: '',
    availability: ''
  });

  const categoryOptions = [
    { value: '', label: 'All Categories' },
    { value: 'endoscopes', label: 'Endoscopes' },
    { value: 'surgical-instruments', label: 'Surgical Instruments' },
    { value: 'imaging-systems', label: 'Imaging Systems' },
    { value: 'accessories', label: 'Accessories' }
  ];

  const manufacturerOptions = [
    { value: '', label: 'All Manufacturers' },
    { value: 'olympus', label: 'Olympus' },
    { value: 'pentax', label: 'Pentax Medical' },
    { value: 'fujifilm', label: 'Fujifilm' },
    { value: 'karl-storz', label: 'Karl Storz' }
  ];

  const certificationOptions = [
    { value: 'gdpmd', label: 'GDPMD Certified' },
    { value: 'iso13485', label: 'ISO 13485' },
    { value: 'ce-mark', label: 'CE Mark' },
    { value: 'fda-approved', label: 'FDA Approved' }
  ];

  const priceRangeOptions = [
    { value: '', label: 'Any Price' },
    { value: 'under-10k', label: 'Under RM 10,000' },
    { value: '10k-50k', label: 'RM 10,000 - 50,000' },
    { value: '50k-100k', label: 'RM 50,000 - 100,000' },
    { value: 'over-100k', label: 'Over RM 100,000' }
  ];

  const availabilityOptions = [
    { value: '', label: 'All Items' },
    { value: 'in-stock', label: 'In Stock' },
    { value: 'pre-order', label: 'Pre-Order' },
    { value: 'custom-order', label: 'Custom Order' }
  ];

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    onFilterChange?.(newFilters);
  };

  const handleCertificationChange = (certValue, checked) => {
    const newCertifications = checked
      ? [...filters?.certification, certValue]
      : filters?.certification?.filter(cert => cert !== certValue);
    
    handleFilterChange('certification', newCertifications);
  };

  const clearAllFilters = () => {
    const clearedFilters = {
      category: '',
      manufacturer: '',
      certification: [],
      priceRange: '',
      availability: ''
    };
    setFilters(clearedFilters);
    onFilterChange?.(clearedFilters);
  };

  const hasActiveFilters = Object.values(filters)?.some(value => 
    Array.isArray(value) ? value?.length > 0 : value !== ''
  );

  return (
    <>
      {/* Mobile Filter Toggle */}
      <div className="lg:hidden mb-4">
        <Button
          variant="outline"
          onClick={onToggle}
          iconName="Filter"
          iconPosition="left"
          className="w-full"
        >
          Filters {hasActiveFilters && `(${Object.values(filters)?.flat()?.filter(Boolean)?.length})`}
        </Button>
      </div>
      {/* Filter Panel */}
      <div className={`
        lg:block lg:sticky lg:top-20 lg:h-fit
        ${isOpen ? 'block' : 'hidden'}
        ${className}
      `}>
        <div className="bg-card border border-border rounded-lg p-6 shadow-medical">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-text-primary flex items-center">
              <Icon name="Filter" size={20} className="mr-2" />
              Filters
            </h3>
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearAllFilters}
                iconName="X"
                iconPosition="left"
              >
                Clear All
              </Button>
            )}
          </div>

          {/* Filter Controls */}
          <div className="space-y-6">
            {/* Category Filter */}
            <div>
              <Select
                label="Category"
                options={categoryOptions}
                value={filters?.category}
                onChange={(value) => handleFilterChange('category', value)}
                placeholder="Select category"
              />
            </div>

            {/* Manufacturer Filter */}
            <div>
              <Select
                label="Manufacturer"
                options={manufacturerOptions}
                value={filters?.manufacturer}
                onChange={(value) => handleFilterChange('manufacturer', value)}
                placeholder="Select manufacturer"
                searchable
              />
            </div>

            {/* Certification Filter */}
            <div>
              <label className="block text-sm font-medium text-text-primary mb-3">
                Certifications
              </label>
              <div className="space-y-2">
                {certificationOptions?.map((cert) => (
                  <Checkbox
                    key={cert?.value}
                    label={cert?.label}
                    checked={filters?.certification?.includes(cert?.value)}
                    onChange={(e) => handleCertificationChange(cert?.value, e?.target?.checked)}
                  />
                ))}
              </div>
            </div>

            {/* Price Range Filter */}
            <div>
              <Select
                label="Price Range"
                options={priceRangeOptions}
                value={filters?.priceRange}
                onChange={(value) => handleFilterChange('priceRange', value)}
                placeholder="Select price range"
              />
            </div>

            {/* Availability Filter */}
            <div>
              <Select
                label="Availability"
                options={availabilityOptions}
                value={filters?.availability}
                onChange={(value) => handleFilterChange('availability', value)}
                placeholder="Select availability"
              />
            </div>
          </div>

          {/* Mobile Apply Button */}
          <div className="lg:hidden mt-6 pt-6 border-t border-border">
            <Button
              variant="default"
              fullWidth
              onClick={onToggle}
              iconName="Check"
              iconPosition="left"
            >
              Apply Filters
            </Button>
          </div>
        </div>

        {/* Active Filters Summary */}
        {hasActiveFilters && (
          <div className="mt-4 p-4 bg-muted rounded-lg">
            <h4 className="text-sm font-medium text-text-primary mb-2">Active Filters:</h4>
            <div className="flex flex-wrap gap-2">
              {filters?.category && (
                <span className="inline-flex items-center px-2 py-1 bg-primary/10 text-primary text-xs rounded-full">
                  {categoryOptions?.find(opt => opt?.value === filters?.category)?.label}
                  <button
                    onClick={() => handleFilterChange('category', '')}
                    className="ml-1 hover:text-primary/80"
                  >
                    <Icon name="X" size={12} />
                  </button>
                </span>
              )}
              {filters?.manufacturer && (
                <span className="inline-flex items-center px-2 py-1 bg-primary/10 text-primary text-xs rounded-full">
                  {manufacturerOptions?.find(opt => opt?.value === filters?.manufacturer)?.label}
                  <button
                    onClick={() => handleFilterChange('manufacturer', '')}
                    className="ml-1 hover:text-primary/80"
                  >
                    <Icon name="X" size={12} />
                  </button>
                </span>
              )}
              {filters?.certification?.map(cert => (
                <span key={cert} className="inline-flex items-center px-2 py-1 bg-success/10 text-success text-xs rounded-full">
                  {certificationOptions?.find(opt => opt?.value === cert)?.label}
                  <button
                    onClick={() => handleCertificationChange(cert, false)}
                    className="ml-1 hover:text-success/80"
                  >
                    <Icon name="X" size={12} />
                  </button>
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default ProductFilter;